import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { COMBINED_FUNNEL_FORM_INPUT_ENTRIES, FILE_TYPE_FIELDS } from './CodeDestination.constants'
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react'
import { CombinedFunnelComponentSteppedInputProps } from '../CombinedFunnelMobile.types'
import { CombinedFunnelMobileCodeDestinationInput } from './CodeDestinationInput'
import { CombinedFunnelMobileCodePreviewWrapper } from '../CodePreviewWrapper'
import { CombinedTextProps, DropdownMenu } from '@dtx-company/shared-components/src'
import { Menu } from '@dtx-company/shared-components/src/components/atoms/Menu/index'
import { MenuItem } from '@dtx-company/shared-components/src/components/atoms/MenuItem/index'
import { ScanDestinationIcon } from '../../CreateFlowcodeForm/CreateFlowcodeFormMobile/ScanDestination/ScanDestinationIcons'
import {
  SocialFormFields,
  SocialFormInputs
} from '@app/pre-build/src/components/OnboardingFlows/Flowtag/OnboardingForms/SocialForm/SocialForm.types'
import { SocialFormInputType } from '@app/pre-build/src/components/OnboardingFlows/Flowtag/OnboardingForms/SocialForm/SocialForm.constants'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { getSocialsLandingPageDestination } from '../CombinedFunnelMobile.utils'
import { setFileName } from '@dtx-company/inter-app/src/redux/slices/OnboardingSocialsSlice'
import { useCombinedFunnelMobileInitializePage } from '../CombinedFunnelMobile.hooks'
import { useDispatch } from 'react-redux'
import { useMobileDestination } from '../CombinedFunnelMobile'
import { useSocialForm } from '@app/pre-build/src/components/OnboardingFlows/Flowtag/OnboardingForms/SocialForm/SocialForm.hooks'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'

export const ScanDestinationLabel: FC<{
  destination: [SocialFormFields, SocialFormInputType]
  labelColor?: CombinedTextProps['color']
  labelVariant?: CombinedTextProps['variant']
  iconColor?: string
  labelProps?: { [key: string]: string }
}> = ({ destination, labelColor, labelVariant, iconColor, labelProps }) => {
  const provider = destination[1]
  const currentPageType = provider.widgetType
  const destinationProvider = FILE_TYPE_FIELDS.includes(destination[0]) ? 'widget' : destination[0]
  const landingPageDestination = getSocialsLandingPageDestination(
    destinationProvider,
    currentPageType
  )
  return (
    <Box width={'100%'} alignItems="center" padding={'10px 0'}>
      <ScanDestinationIcon destination={landingPageDestination} color={iconColor} />
      <Spacer mr="8px" />
      <Text
        fontSize={'14px'}
        lineHeight="16px"
        color={labelColor}
        variant={labelVariant}
        style={{ whiteSpace: 'nowrap' }}
        {...labelProps}
      >
        {provider.label}
      </Text>
    </Box>
  )
}

export const CombinedFunnelMobileCodeDestination: FC<CombinedFunnelComponentSteppedInputProps> = ({
  advanceStep,
  variant
}) => {
  const { destination, setDestination, setValue } = useMobileDestination()
  const [savedDestinationLoaded, setSavedDestinationLoaded] = useState(false)

  useCombinedFunnelMobileInitializePage()
  const dispatch = useDispatch()
  const {
    values,
    errors,
    handleChange: getOnChangeValue,
    ...socialFormProps
  } = useSocialForm({
    advanceStep,
    isCombinedFunnelMobile: true
  })

  const onChangeValue = useMemo(
    () => getOnChangeValue(destination?.[0]),
    [getOnChangeValue, destination]
  )

  useEffect(() => {
    if (!savedDestinationLoaded) {
      const savedDestinationKey = Object.keys(values).find(key => {
        return values[key as keyof SocialFormInputs]
      })
      const savedDestination = COMBINED_FUNNEL_FORM_INPUT_ENTRIES.filter(
        entry => entry[0] === savedDestinationKey
      )
      if (savedDestination.length) {
        setDestination(savedDestination[0])
      }
      setSavedDestinationLoaded(true)
    }
  }, [savedDestinationLoaded, values, setDestination])

  const allowNextBtn = useMemo(() => Object.values(values).some(value => Boolean(value)), [values])

  const handleChangeDestination = (
    newDestination: [SocialFormFields, SocialFormInputType]
  ): void => {
    //updates the value of the 2nd step form, for the field corresponding to this destination
    onChangeValue({
      target: { value: '' }
    } as ChangeEvent<HTMLInputElement>)
    dispatch(setFileName({ type: 'all', fileName: '' }))
    //stores the selected destination for this form in a page wide context
    setDestination(newDestination)
    events.Flowcode_Combined_Funnel_Mobile_Homepage_Selected_Destination({
      destination: newDestination[0],
      variant,
      screen: 'code_destination'
    })
  }

  return (
    <CombinedFunnelMobileCodePreviewWrapper>
      <Box p="0 32px" flexDirection="column">
        <Text variant={'display/small'}>
          Step 1 of 2: Choose your destination, then enter a link or file
        </Text>
        <Box mt="24px" flexDirection={'column'}>
          <DropdownMenu
            header={
              <Box
                paddingLeft={'8px'}
                width="100%"
                data-testid={`selected-${destination[1].label}`}
              >
                <ScanDestinationLabel
                  destination={destination}
                  labelColor="inherit"
                  labelVariant="button/pageNav"
                />
              </Box>
            }
            activeBackgroundColor={'secondary.backgroundActive'}
            color="primary.flowBlue"
            position={'relative'}
            wrapperProps={{
              borderRadius: '4px 4px 0 0',
              boxShadow: '3px 3px 30px rgba(17, 17, 17, 0.1)'
            }}
            borderRadius="inherit"
            height="44px"
            borderBottom={'none'}
          >
            <Menu backgroundColor={'primary.white'} width="100%" position={'absolute'} zIndex={1}>
              {COMBINED_FUNNEL_FORM_INPUT_ENTRIES.filter(scanDestination => {
                return destination[1].label !== scanDestination[1].label
              }).map(menuItemDestination => {
                return (
                  <MenuItem
                    onClick={() => handleChangeDestination(menuItemDestination)}
                    key={menuItemDestination[1].label}
                    padding={'0 10px'}
                    height="44px"
                    data-testid={`item-${menuItemDestination[1].label}`}
                  >
                    <ScanDestinationLabel destination={menuItemDestination} iconColor="#000" />
                  </MenuItem>
                )
              })}
            </Menu>
          </DropdownMenu>
          <CombinedFunnelMobileCodeDestinationInput
            currentDestination={destination}
            disabledNextBtn={!allowNextBtn}
            values={values}
            errors={errors}
            handleChange={e => {
              //handles value changing for this form
              onChangeValue(e)
              //stores the value of the input in a page-wide context
              setValue(e.target.value)
            }}
            {...socialFormProps}
          />
        </Box>
      </Box>
    </CombinedFunnelMobileCodePreviewWrapper>
  )
}
